import { useCallback } from 'react';
import { SaturationDataUnformatted } from '@blisspointmedia/bpm-types/dist/BrandEquity';
import { downloadPNG, exportToExcel } from '@src/utils/download-utils';

// The unformatted spend-decay rows exported to Excel. Owned here (rather than in a page
// component) so both Model Insights widgets and this hook share one definition.
export interface DecayDataUnformatted {
  client: string;
  kpi: string;
  refresh_date: string;
  period: number;
  name: string;
  channel: string;
  platform: string;
  tactic: string;
  brand_nonbrand: string;
  incrementality_test: boolean;
  spend_carryover: number;
  max_lag: number;
}

interface SaturationDecayDownloads {
  excelDownloadSat: () => void;
  pngDownloadSat: () => Promise<void>;
  excelDownloadDecay: () => void;
  pngDownloadDecay: () => Promise<void>;
}

// The four identical saturation/decay download callbacks shared by both Model Insights
// widgets. PNG targets the `.left`/`.right` chart cards under each page's unique root
// (`rootSelector`, e.g. `.brand-impact-model-insights`); Excel exports the raw data.
export const useSaturationDecayDownloads = (
  company: string,
  saturationDataUnformatted: SaturationDataUnformatted[],
  decayDataUnformatted: DecayDataUnformatted[],
  rootSelector: string
): SaturationDecayDownloads => {
  const excelDownloadSat = useCallback(() => {
    exportToExcel(saturationDataUnformatted, `${company}_saturation`);
  }, [saturationDataUnformatted, company]);

  const pngDownloadSat = useCallback(async () => {
    await downloadPNG(`${rootSelector} .left .chart-container`, `${company}_saturation`);
  }, [company, rootSelector]);

  const excelDownloadDecay = useCallback(() => {
    exportToExcel(decayDataUnformatted, `${company}_decay`);
  }, [decayDataUnformatted, company]);

  const pngDownloadDecay = useCallback(async () => {
    await downloadPNG(`${rootSelector} .right .chart-container`, `${company}_decay`);
  }, [company, rootSelector]);

  return { excelDownloadSat, pngDownloadSat, excelDownloadDecay, pngDownloadDecay };
};
